import React, { useState, useRef, useEffect } from 'react'
import { BsInfoCircle } from 'react-icons/bs'

interface InfoButtonProps {
  content: string
}

const InfoButton: React.FC<InfoButtonProps> = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<'top' | 'bottom'>('top')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen && buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const tooltipHeight = tooltipRef.current.offsetHeight
      const spaceAbove = buttonRect.top
      const spaceBelow = window.innerHeight - buttonRect.bottom
      const headerHeight = 80 // Altura aproximada do header

      // Se não há espaço suficiente acima ou o tooltip ficaria coberto pelo header, mostra embaixo
      if (spaceAbove < tooltipHeight + 10 || spaceAbove < headerHeight + 20) {
        setPosition('bottom')
      } else {
        setPosition('top')
      }
    }
  }, [isOpen])

  const handleClickOutside = (e: MouseEvent) => {
    if (
      tooltipRef.current &&
      buttonRef.current &&
      !tooltipRef.current.contains(e.target as Node) &&
      !buttonRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  return (
    <div className="relative inline-flex items-center ml-2">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        className="flex items-center justify-center w-5 h-5 border-2 border-primary-600 rounded-full bg-transparent hover:bg-primary-600 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 group"
        aria-label="Mais informações"
      >
        <BsInfoCircle className="w-3 h-3 text-primary-600 group-hover:text-white transition-colors duration-200" />
      </button>
      {isOpen && (
        <div
          ref={tooltipRef}
          className={`absolute ${
            position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          } left-1/2 transform -translate-x-1/2 z-50`}
        >
          <div className="w-[280px] sm:w-[320px] md:min-w-[400px] md:max-w-[500px] lg:min-w-[500px] lg:max-w-[600px] px-4 py-4 sm:px-5 sm:py-5 md:px-6 md:py-6 lg:px-7 lg:py-8 bg-white border border-gray-200 rounded-lg shadow-lg text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words max-h-[70vh] overflow-y-auto">
            {content}
            <div
              className={`absolute ${
                position === 'top' ? 'top-full' : 'bottom-full'
              } left-1/2 transform -translate-x-1/2 ${position === 'top' ? '-mt-px' : '-mb-px'}`}
            >
              <div
                className={`w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent ${
                  position === 'top'
                    ? 'border-t-[6px] border-t-white'
                    : 'border-b-[6px] border-b-white'
                }`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InfoButton
